import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography
} from '@/ui';
import {
  ArrowForwardIcon,
  CheckIcon,
  CloseIcon,
  ExpandMoreIcon,
  InfoOutlinedIcon,
  MuiTextField,
  ScheduleIcon,
  WarningAmberIcon
} from '@/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpClient } from '@/shared/api/http';
import { lenderConsentEndpoints } from '@/shared/api/endpoints';
import { AuthService } from '@/shared/api/services/authService';
import InfoDrawer from '@/components/InfoDrawer';
import FAQPanel from '@/components/FAQPanel';
import termsJson from '@/knowledge/terms.json';
import privacyJson from '@/knowledge/privacy.json';
import { infoContentSchema } from '@/shared/types/infoContent';
import { faqTopics } from '@/shared/help/faqTopics';
import type { FaqContent } from '@/shared/help/faqTypes';
import { getLenderLogo } from '@/shared/lib/utils/lenderLogos';

// ─── Types ───────────────────────────────────────────────────────────────────

interface InvitationResponse {
  id: number;
  userId: number;
  verifyCustomer: 'Y' | 'N' | string;
  noOfAttempts: number;
  lenderName?: string;
  organizationType?: string;
  organisationType?: string;
  status?: string;
  expiryDate?: string;
  expiredDate?: string;
  createdDate?: string;
  createdAt?: string;
  lastUpdateDate?: string;
  updatedAt?: string;
  tncFlag?: string;
}

const resolveOrganisationType = (source: {
  organizationType?: string;
  organisationType?: string;
} | null | undefined): string => {
  const raw = source?.organizationType ?? source?.organisationType;
  return typeof raw === 'string' ? raw.trim() : '';
};

interface PostcodeVerifyResponse {
  id: number;
  userId: number;
  postcode: string;
  verifiedCustomer: 'Y' | 'N' | string;
  noOfAttempts?: number;
  status?: string;
  message?: string;
  errorMessage?: string;
}

interface StatusUpdateRequest {
  id: number;
  userId: number;
  status: 'ACCEPTED' | 'DECLINED';
  termsAndConditionsAccepted?: 'Y';
}

interface StatusUpdateResponse {
  id: number;
  userId: number;
  status: string;
  termsAndConditionsAccepted?: string;
}

// ─── Person with X Icon ───────────────────────────────────────────────────────

function PersonXIcon(): JSX.Element {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Head */}
      <circle cx="10" cy="7" r="3.5" fill="#FFFFFF" />
      {/* Body */}
      <path
        d="M2.5 19c0-3.3 3.35-6 7.5-6s7.5 2.7 7.5 6H2.5z"
        fill="#FFFFFF"
      />
      {/* X mark */}
      <path
        d="M16 5.5L20 9.5M20 5.5L16 9.5"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Helper: Format timestamp (e.g. 26 August 2026 at 10:14.) ─────────────────

const formatPostcodeExpiredTimestamp = (dateInput?: string | Date): string => {
  const d = dateInput ? new Date(dateInput) : new Date();
  const validDate = !isNaN(d.getTime()) ? d : new Date();
  return (
    validDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) +
    ' at ' +
    validDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) +
    '.'
  );
};

// ─── Carousel images ─────────────────────────────────────────────────────────

const carouselImages = [
  '/assets/onBoarding/crausal4.webp',
  '/assets/onBoarding/crausal2.webp',
  '/assets/onBoarding/crausal3.webp',
  '/assets/onBoarding/crausal1.webp'
];

// ─── Card UI states ───────────────────────────────────────────────────────────

type ConsentFlowState =
  | 'loading'          // initial token validation / invitation fetch
  | 'confirm_postcode' // postcode verification required
  | 'decision'         // main allow/decline card
  | 'allowed'          // budget shared successfully
  | 'declined'         // user declined
  | 'expired'          // link has expired (token invalid / 401)
  | 'incorrect_postcode'; // postcode failed / account locked

export default function LenderConsentScreen(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The `pcode` query param carries the invitation token
  const consentToken = searchParams.get('pcode') ?? '';

  // ── State ──────────────────────────────────────────────────────────────────
  const [cardState, setCardState] = useState<ConsentFlowState>('loading');
  const [invitationId, setInvitationId] = useState<number | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [_tncFlag, setTncFlag] = useState<string | null>(null);
  const [organisationName, setOrganisationName] = useState<string>('');
  const [organisationType, setOrganisationType] = useState<string>('');
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [postcodeError, setPostcodeError] = useState<string | null>(null);
  const [postcodeAttempts, setPostcodeAttempts] = useState<number>(0);
  const [isPostcodeWrong, setIsPostcodeWrong] = useState(false);
  const [postcodeAlertOpen, setPostcodeAlertOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>('why_asked');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [confirmedAt, setConfirmedAt] = useState<string>('');
  const [postcodeExpiredAt, setPostcodeExpiredAt] = useState<string>('');
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);

  const termsContent = useMemo(() => infoContentSchema.parse(termsJson), []);
  const privacyContent = useMemo(() => infoContentSchema.parse(privacyJson), []);
  const faqContent: FaqContent = useMemo(() => {
    const baseItems = faqTopics.welcome?.items ?? [];
    return {
      items: baseItems.map((item) => ({
        ...item,
        answer: item.answer.replace('[creditor/water company/etc.]', organisationName || 'your creditor')
      }))
    };
  }, [organisationName]);

  const lenderInitials = useMemo(() => {
    if (!organisationName) return 'RB';
    const words = organisationName.trim().split(/\s+/).filter(Boolean);
    const firstWord = words[0];
    if (!firstWord) return 'RB';
    if (words.length === 1) {
      return firstWord.slice(0, 2).toUpperCase();
    }
    const secondWord = words[1];
    const char1 = firstWord.charAt(0);
    const char2 = secondWord ? secondWord.charAt(0) : '';
    return (char1 + char2).toUpperCase() || 'RB';
  }, [organisationName]);

  const { headerTop, headerBottom } = useMemo(() => {
    const org = (organisationName || '').trim();
    const parts = org.split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      const last = parts[parts.length - 1] ?? '';
      return {
        headerTop: parts.slice(0, -1).join(' '),
        headerBottom: last
      };
    }
    return {
      headerTop: org,
      headerBottom: ''
    };
  }, [organisationName]);

  const [logoLoadError, setLogoLoadError] = useState(false);
  const lenderLogo = useMemo(() => getLenderLogo(organisationName), [organisationName]);

  useEffect(() => {
    setLogoLoadError(false);
  }, [organisationName]);

  // ── JWT obtained by exchanging the `p` token via signInWithToken ──────────
  // Stored in a ref so it's available in all async handlers without being
  // a reactive dependency (avoids re-running effects on every render).
  const jwtRef = useRef<string | null>(null);
  const leftSectionRef = useRef<HTMLDivElement | null>(null);

  // ── Helper: make API calls with the exchanged JWT as Bearer ───────────────
  const tokenHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${jwtRef.current ?? ''}` },
    skipAuth: true as const
  }), []);

  const applyInvitationOrgDetails = useCallback((inv: InvitationResponse): void => {
    if (inv.lenderName) setOrganisationName(inv.lenderName);
    const orgType = resolveOrganisationType(inv);
    if (orgType) setOrganisationType(orgType);
  }, []);

  // ── Step 1: Exchange p token → JWT, then fetch invitation ─────────────────
  useEffect(() => {
    if (!consentToken) {
      setCardState('expired');
      return;
    }

    const init = async () => {
      const consentTokenHeaders = {
        headers: { Authorization: `Bearer ${consentToken}` },
        skipAuth: true as const
      };

      try {
        // Same pattern as the OB flow: exchange the magic-link token for a JWT
        const authResponse = await AuthService.signInWithToken(
          { token: consentToken },
          { skipErrorCapture: true }
        );
        jwtRef.current = authResponse.access_token;
      } catch {
        // Token invalid / expired — still try to load invitation metadata for the card
        /*try {
          const metaRes = await httpClient.get<InvitationResponse>(
            lenderConsentEndpoints.getInvitation,
            consentTokenHeaders
          );
          applyInvitationOrgDetails(metaRes.data);
        } catch {
          // Ignore metadata fetch failures; expired UI will use defaults
        }*/
        setCardState('expired');
        return;
      }

      try {
        // Fetch the invitation details using the exchanged JWT
        const res = await httpClient.get<InvitationResponse>(
          lenderConsentEndpoints.getInvitation,
          tokenHeaders()
        );

        const inv = res.data;
        setInvitationId(inv.id);
        setUserId(inv.userId);
        if (inv.tncFlag) {
          setTncFlag(inv.tncFlag);
        }

        applyInvitationOrgDetails(inv);

        const attemptsCount =
          typeof inv.noOfAttempts === 'number' ? inv.noOfAttempts : Number(inv.noOfAttempts) || 0;
        setPostcodeAttempts(attemptsCount);

        const statusUpper = String(inv.status ?? '').toUpperCase();

        // If attempts are already exceeded (> 3) or status is locked / postcode expired
        if (
          attemptsCount > 3 ||
          statusUpper === 'LOCKED' ||
          statusUpper === 'POSTCODE_EXPIRED' ||
          (statusUpper === 'EXPIRED' && attemptsCount > 3)
        ) {
          const rawDate = inv.lastUpdateDate || inv.updatedAt || inv.expiryDate || inv.expiredDate || inv.createdDate || inv.createdAt;
          setPostcodeExpiredAt(formatPostcodeExpiredTimestamp(rawDate));
          setCardState('incorrect_postcode');
          return;
        }

        if (statusUpper === 'EXPIRED') {
          setCardState('expired');
          return;
        }
        if (statusUpper === 'ACCEPTED' || statusUpper === 'DECLINED') {
          const rawCreated = inv.createdDate || inv.createdAt || inv.lastUpdateDate || inv.updatedAt;
          if (rawCreated) {
            const d = new Date(rawCreated);
            if (!isNaN(d.getTime())) {
              setConfirmedAt(
                d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) +
                ' at ' +
                d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + '.'
              );
            }
          }
          setCardState(statusUpper === 'ACCEPTED' ? 'allowed' : 'declined');
          return;
        }

        const requiresVerify = String(inv.verifyCustomer ?? '').toUpperCase() === 'Y';
        setCardState(requiresVerify ? 'confirm_postcode' : 'decision');
      } catch (err: unknown) {
        const errorData = (err as {
          response?: {
            data?: InvitationResponse & {
              errorMessage?: string;
              message?: string;
              noOfAttempts?: number;
              status?: string;
            };
            status?: number;
          };
        })?.response?.data;

        if (errorData) {
          applyInvitationOrgDetails(errorData);
        }

        const status = (err as { response?: { status?: number } })?.response?.status;
        const errAttempts = typeof errorData?.noOfAttempts === 'number' ? errorData.noOfAttempts : null;
        const errMsg = [
          errorData?.errorMessage,
          errorData?.message,
          typeof errorData === 'string' ? errorData : ''
        ]
          .join(' ')
          .toLowerCase();

        if (
          (errAttempts !== null && errAttempts > 3) ||
          errMsg.includes('three times') ||
          errMsg.includes('3 times') ||
          (errMsg.includes('postcode') && errMsg.includes('locked')) ||
          (errMsg.includes('postcode') && errMsg.includes('expired'))
        ) {
          setPostcodeExpiredAt(formatPostcodeExpiredTimestamp());
          setCardState('incorrect_postcode');
          return;
        }

        if (status === 401 || status === 403 || status === 404) {
          setCardState('expired');
        } else {
          setApiError('Unable to load the request. Please try again later.');
          setCardState('expired');
        }
      }
    };

    void init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentToken]);

  // ── Carousel auto-advance ──────────────────────────────────────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // ── Carousel swipe ─────────────────────────────────────────────────────────
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const onTouchStartCarousel = (event: React.TouchEvent<HTMLDivElement>): void => {
    const t = event.touches.item(0);
    if (t) touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEndCarousel = (event: React.TouchEvent<HTMLDivElement>): void => {
    const start = touchStartRef.current;
    if (!start) return;
    const t = event.changedTouches.item(0);
    if (!t) return;
    const dx = t.clientX - start.x;
    if (Math.abs(dx) > 40) {
      setCurrentImageIndex((prev) =>
        dx > 0 ? (prev - 1 + carouselImages.length) % carouselImages.length : (prev + 1) % carouselImages.length
      );
    }
    touchStartRef.current = null;
  };

  // ── Postcode verification ──────────────────────────────────────────────────
  const handleVerifyPostcode = async () => {
    const trimmed = postcode.trim();
    if (!trimmed) {
      setPostcodeError('Please enter your postcode');
      return;
    }
    if (invitationId === null) return;

    setPostcodeError(null);
    setIsPostcodeWrong(false);
    setIsVerifying(true);

    const markPostcodeExpired = () => {
      setIsPostcodeWrong(true);
      setPostcodeAlertOpen(false);
      setPostcodeExpiredAt(formatPostcodeExpiredTimestamp());
      setCardState('incorrect_postcode');
    };

    try {
      const res = await httpClient.put<PostcodeVerifyResponse>(
        lenderConsentEndpoints.verifyPostcode,
        { id: invitationId, postcode: trimmed.toLowerCase() },
        tokenHeaders()
      );

      const resData = res.data;
      const backendAttempts =
        typeof resData?.noOfAttempts === 'number'
          ? resData.noOfAttempts
          : typeof (resData as unknown as Record<string, unknown>)?.data === 'object' &&
            typeof ((resData as unknown as Record<string, unknown>)?.data as Record<string, unknown>)?.noOfAttempts === 'number'
          ? (((resData as unknown as Record<string, unknown>)?.data as Record<string, unknown>)?.noOfAttempts as number)
          : null;

      const currentAttempts = backendAttempts !== null ? backendAttempts : postcodeAttempts + 1;
      setPostcodeAttempts(currentAttempts);

      const statusUpper = String(resData?.status ?? '').toUpperCase();
      const isLocked = currentAttempts > 3 || statusUpper === 'LOCKED' || statusUpper === 'EXPIRED';

      // A 2xx response with verifiedCustomer !== 'Y' means the postcode did
      // not match — treat it the same as a failed verification.
      if (String(resData?.verifiedCustomer ?? '').toUpperCase() !== 'Y') {
        if (isLocked) {
          markPostcodeExpired();
          return;
        }
        setIsPostcodeWrong(true);
        setPostcodeAlertOpen(false);
        setTimeout(() => setPostcodeAlertOpen(true), 10);
        return;
      }

      if (isLocked) {
        markPostcodeExpired();
        return;
      }

      // Success: move to decision
      setIsPostcodeWrong(false);
      setPostcodeAlertOpen(false);
      setCardState('decision');
      setExpandedAccordion('why_asked');
    } catch (err: unknown) {
      const nextAttempts = postcodeAttempts + 1;
      setPostcodeAttempts(nextAttempts);

      const errObj = err as { response?: { status?: number; data?: unknown } };
      const status = errObj?.response?.status;
      const raw = errObj?.response?.data;

      let errorText = '';
      let errorAttempts: number | null = null;
      let errorStatus = '';

      if (typeof raw === 'string') {
        errorText = raw;
      } else if (raw && typeof raw === 'object') {
        const d = raw as Record<string, unknown>;
        errorText = [d.errorMessage, d.message, d.errorTitle, d.code, d.errorCode, d.title]
          .filter((v): v is string => typeof v === 'string')
          .join(' ');
        if (typeof d.noOfAttempts === 'number') {
          errorAttempts = d.noOfAttempts;
        }
        if (typeof d.status === 'string') {
          errorStatus = d.status;
        }
      }

      const lower = errorText.toLowerCase();
      const isStatusLocked = status === 423 || status === 403;
      const isTextLocked =
        lower.includes('locked') ||
        lower.includes('three times') ||
        lower.includes('3 times') ||
        lower.includes('exceeded') ||
        (lower.includes('postcode') && lower.includes('expired')) ||
        lower.includes('no longer be used');
      const attemptsExceeded = (errorAttempts !== null && errorAttempts > 3) || nextAttempts > 3;
      const isErrorStatusLocked = errorStatus.toUpperCase() === 'LOCKED' || errorStatus.toUpperCase() === 'EXPIRED';

      if (isStatusLocked || isTextLocked || attemptsExceeded || isErrorStatusLocked) {
        markPostcodeExpired();
      } else {
        setIsPostcodeWrong(true);
        setPostcodeAlertOpen(false);
        setTimeout(() => setPostcodeAlertOpen(true), 10);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Allow / Decline submission ─────────────────────────────────────────────
  const handleDecision = async (decision: 'ACCEPTED' | 'DECLINED') => {
    if (invitationId === null || userId === null) return;

    setIsSubmitting(true);
    setApiError(null);

    const payload: StatusUpdateRequest = {
      id: invitationId,
      userId,
      status: decision,
      ...(decision === 'ACCEPTED' ? { termsAndConditionsAccepted: 'Y' } : {})
    };

    try {
      await httpClient.put<StatusUpdateResponse>(
        lenderConsentEndpoints.updateStatus,
        payload,
        tokenHeaders()
      );

      if (decision === 'ACCEPTED') {
        const now = new Date();
        const formatted = now.toLocaleDateString('en-GB', {
          day: 'numeric', month: 'long', year: 'numeric'
        }) + ' at ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + '.';
        setConfirmedAt(formatted);
        setCardState('allowed');
      } else {
        const now = new Date();
        const formatted = now.toLocaleDateString('en-GB', {
          day: 'numeric', month: 'long', year: 'numeric'
        }) + ' at ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + '.';
        setConfirmedAt(formatted);
        setDeclineDialogOpen(false);
        setCardState('declined');
      }
    } catch (err: unknown) {
      const errorMsg =
        (err as { response?: { data?: { errorMessage?: string; message?: string } } })?.response?.data?.errorMessage ||
        (err as { response?: { data?: { errorMessage?: string; message?: string } } })?.response?.data?.message;
      setApiError(errorMsg || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // ── Shared UI sub-components ───────────────────────────────────────────────
  const renderLenderContactCard = () => (
    <Box data-testid="lender-contact-card" sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', mb: 2.5, textAlign: 'left', backgroundColor: '#FFFFFF' }}>
      <Box sx={{ p: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: '8px', backgroundColor: '#003F44',
          color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 700, flexShrink: 0
        }}>
          {lenderInitials}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#111827', lineHeight: 1.2 }}>
            {organisationName}
          </Typography>
          {organisationType && (
            <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.25 }}>{organisationType}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  // PB-344: fall back to a generic label so the decline texts have no gap when the company name is empty
  const declineOrgLabel = organisationName.trim() || 'the company';

  const renderBottomTealAction = (
    variant: 'allowed' | 'declined' | 'incorrect_postcode' | 'expired' = 'allowed',
  ) => {
    const title =
      variant === 'declined'
        ? 'If you change your mind'
        : variant === 'incorrect_postcode' || variant === 'expired'
          ? 'You can still share your budget'
          : 'See your budget any time';

    const body =
      variant === 'declined'
        ? `You can still share it later - ask ${declineOrgLabel} for a new link, or sign in to IE Hub and share it from your account.`
        : variant === 'incorrect_postcode' || variant === 'expired'
          ? `Sign in to IE Hub and share your latest budget${organisationName.trim() ? ` with ${organisationName.trim()}` : ''} from your account.` // PB-343: company name optional
          : 'Sign in to IE Hub to view your budget, keep it up to date, and see who you have shared it with.';

    return (
      <Box sx={{ backgroundColor: '#003F44', borderRadius: '12px', p: 2, textAlign: 'left' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#FFFFFF', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#E2E8F0', lineHeight: 1.45, mb: 1.75 }}>
          {body}
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate('/auth')}
          data-testid="lender-consent-btn-signin"
          sx={{
            backgroundColor: '#FFFFFF', color: '#003F44', height: '42px', borderRadius: '8px',
            fontSize: '13.5px', fontWeight: 700, textTransform: 'none', boxShadow: 'none',
            display: 'flex', justifyContent: 'space-between', px: 2,
            '&:hover': { backgroundColor: '#F8FAFC', boxShadow: 'none' }
          }}
        >
          <span>Sign in to IE Hub</span>
          <ArrowForwardIcon sx={{ fontSize: '16px', transform: 'rotate(-45deg)' }} />
        </Button>
      </Box>
    );
  };

  // ── Co-branding header (outside card) ─────────────────────────────────────
  const renderCardHeader = () => {
    const showLogo = Boolean(lenderLogo) && !logoLoadError;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3.5, mt: { xs: 1, md: 0 } }}>
        {showLogo ? (
          <Box
            component="img"
            src={lenderLogo ?? ''}
            alt={organisationName}
            onError={() => setLogoLoadError(true)}
            data-testid="lender-consent-header-lender-logo"
            sx={{
              maxHeight: 38,
              maxWidth: 140,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              mr: 1.5
            }}
          />
        ) : (
          <Box
            data-testid="lender-consent-header-lender-text"
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1.5 }}
          >
            <Typography sx={{ fontFamily: '"Georgia", "Times New Roman", serif', fontWeight: 700, fontSize: '18px', lineHeight: 1.15, color: '#003F44', letterSpacing: '-0.2px', textAlign: 'right' }}>
              {headerTop}
            </Typography>
            {headerBottom ? (
              <Typography sx={{ fontFamily: '"Georgia", "Times New Roman", serif', fontWeight: 700, fontSize: '14px', lineHeight: 1.1, color: '#003F44', letterSpacing: '0.8px', mt: 0.2, textAlign: 'right' }}>
                {headerBottom}
              </Typography>
            ) : null}
          </Box>
        )}
        {cardState != 'expired' && (
          <Box sx={{ width: '2px', height: '36px', backgroundColor: '#FCC117', mx: 1.25, borderRadius: '1px' }} />
        )}
        <Box component="img" src="/icons/logo.png" alt="IE Hub" sx={{ height: 38, width: 'auto', objectFit: 'contain' }} />
      </Box>
    );
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  const renderLoading = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#003F44' }} />
      <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>Loading your request…</Typography>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      data-testid="lender-consent-screen-wrapper"
      sx={{
        minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'row',
        overflow: 'hidden', position: 'relative', backgroundColor: '#F5F5F5'
      }}
    >
      {/* ── Left Section ──────────────────────────────────────────────────── */}
      <Box
        ref={leftSectionRef}
        data-testid="lender-consent-left-section"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          backgroundColor: '#F5F5F5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          height: '100vh',
          overflowY: declineDialogOpen ? 'hidden' : 'auto',
          overflowX: 'hidden',
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 }
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 450,
            mx: 'auto',
            my: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {renderCardHeader()}

          <Paper
            elevation={0}
            data-testid="lender-consent-card"
            sx={{
              width: '100%', backgroundColor: '#FFFFFF',
              borderRadius: '24px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
              border: '1px solid #ECEFF2',
              p: cardState === 'decision' ? 0 : { xs: 2.75, sm: 3.5, md: 4 },
              display: 'flex', flexDirection: 'column', position: 'relative',
              overflow: cardState === 'decision' ? 'hidden' : undefined,
              maxHeight: cardState === 'decision' ? { xs: 'calc(100vh - 110px)', md: 'min(720px, calc(100vh - 80px))' } : undefined,
            }}
          >

          {/* ── SCREEN: Loading ─────────────────────────────────────────── */}
          {cardState === 'loading' && renderLoading()}

          {/* ── SCREEN: Postcode ────────────────────────────────────────── */}
          {cardState === 'confirm_postcode' && (
            <Box data-testid="lender-consent-state-confirm-postcode" sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '21px', color: '#003F44', lineHeight: 1.25 }}>
                  Confirm your postcode
                </Typography>
                <Tooltip title="Help & FAQs" arrow placement="top">
                  <IconButton
                    size="small"
                    aria-label="Frequently Asked Questions"
                    onClick={() => setFaqOpen(true)}
                    data-testid="lender-consent-btn-postcode-faq"
                    sx={{ p: 0.25, color: '#64748B', '&:hover': { color: '#003F44' } }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '19px' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '13.5px', lineHeight: 1.5, mb: 2.75 }}>
                {organisationName} has asked to access your latest budget. We check your postcode first, so only you can see the request. Nothing is shared at this stage.
              </Typography>

              {/* Postcode field */}
              <Box sx={{ mb: 2.75 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#0A2540', mb: 0.75 }}>Postcode (Home address)</Typography>
                <MuiTextField
                  fullWidth
                  placeholder="e.g. SW1A 1AA"
                  value={postcode}
                  onChange={(e) => {
                    setPostcode(e.target.value.toUpperCase());
                    if (isPostcodeWrong) setIsPostcodeWrong(false);
                    if (postcodeError) setPostcodeError(null);
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleVerifyPostcode(); } }}
                  error={isPostcodeWrong || Boolean(postcodeError)}
                  helperText={postcodeError}
                  FormHelperTextProps={{ sx: { fontSize: '12px', mt: 0.5, fontWeight: 500 } }}
                  inputProps={{ 'data-testid': 'lender-consent-input-postcode', maxLength: 10, autoCapitalize: 'characters' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px', backgroundColor: '#FFFFFF',
                      '& fieldset': { borderColor: (isPostcodeWrong || postcodeError) ? '#EF4444' : '#D1D5DB' },
                      '&:hover fieldset': { borderColor: (isPostcodeWrong || postcodeError) ? '#DC2626' : '#9CA3AF' },
                      '&.Mui-focused fieldset': { borderColor: (isPostcodeWrong || postcodeError) ? '#EF4444' : '#003F44', borderWidth: '1.5px' }
                    },
                    '& input': { py: 1.4, px: 1.75, fontSize: '15px', color: '#1F2937' },
                    '& input::placeholder': { color: '#9CA3AF', opacity: 1 }
                  }}
                />
              </Box>

              {/* 2-step progress box */}
              <Box data-testid="lender-consent-stepper-box" sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.25, backgroundColor: '#FFFFFF', mb: 3 }}>
                {/* Step 1 */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#003F44', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0, mt: 0.25, mr: 1.5 }}>
                    1
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#003F44' }}>Confirm it&apos;s you</Typography>
                      <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#E2E8F0', color: '#334155', px: 0.85, py: 0.25, borderRadius: '4px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                        YOU ARE HERE
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '12.5px', color: '#64748B', lineHeight: 1.45, mt: 0.5 }}>
                      Enter your postcode above. This only unlocks the request — nothing is shared yet.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ width: '1.5px', height: '24px', backgroundColor: '#E5E7EB', ml: '11px', my: 0.5 }} />

                {/* Step 2 */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', border: '1.5px solid #CBD5E1', backgroundColor: '#FFFFFF', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, flexShrink: 0, mt: 0.25, mr: 1.5 }}>
                    2
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#003F44' }}>Make your decision</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#64748B', lineHeight: 1.45, mt: 0.5 }}>
                      See what {organisationName} is asking for, then choose to allow or decline. Your response isn&apos;t complete until you decide.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Button
                fullWidth variant="contained"
                onClick={() => void handleVerifyPostcode()}
                disabled={isVerifying}
                data-testid="lender-consent-btn-verify"
                sx={{
                  backgroundColor: '#003F44', color: '#FFFFFF', height: '48px', borderRadius: '8px',
                  fontSize: '15px', fontWeight: 600, textTransform: 'none', boxShadow: 'none',
                  '&:hover': { backgroundColor: '#002B2E', boxShadow: 'none' }
                }}
              >
                {isVerifying ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Verify address & Continue'}
              </Button>
            </Box>
          )}

          {/* ── SCREEN: Decision ────────────────────────────────────────── */}
          {cardState === 'decision' && (
            <Box
              data-testid="lender-consent-state-decision"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                width: '100%',
              }}
            >
              {/* Scrollable content */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  px: { xs: 2.75, sm: 3.5, md: 4 },
                  pt: { xs: 2.75, sm: 3.5, md: 4 },
                  pb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '19px', color: '#111827', lineHeight: 1.3, mb: 1 }}>
                  {organisationName} has requested to access your latest budget
                </Typography>

                <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '12.5px', lineHeight: 1.5, mb: 2.5 }}>
                  {organisationName} would like to access your latest budget to review your income and expenditure. Please give your consent so they can view your up-to-date financial summary.
                </Typography>

                {/* WHO IS ASKING */}
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                  WHO IS ASKING
                </Typography>

                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '10px', p: 1.75, mb: 2.5, backgroundColor: '#FFFFFF' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#003F44', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                        {lenderInitials}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: '#111827', lineHeight: 1.2 }}>{organisationName}</Typography>
                        {organisationType && (
                          <Typography sx={{ fontSize: '11.5px', color: '#6B7280', mt: 0.2 }}>{organisationType}</Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* BEFORE YOU DECIDE */}
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                  BEFORE YOU DECIDE
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {[
                    {
                      key: 'why_asked',
                      label: 'Why am I being asked?',
                      paragraphs: [
                        `${organisationName} is asking to see your latest budget. They need your permission before they can view it.`,
                        'This helps you stay in control of who can view your latest financial information.'
                      ]
                    },
                    {
                      key: 'what_will_see',
                      label: `What will ${organisationName} see?`,
                      paragraphs: [
                        `If you allow access, ${organisationName} can view the latest version of your budget. This includes your income, household bills, living costs, debts, repayments and other financial information you’ve added.`,
                        'This request does not give any other organisation access.'
                      ]
                    },
                    {
                      key: 'what_happens_if_decline',
                      label: 'What happens if I don’t allow access?',
                      paragraphs: [
                        `${organisationName} will not be able to view your latest budget. If you’ve shared your budget with them before, they may continue using the information they already have.`,
                        'You’re in control',
                        `You decide whether ${organisationName} can view your latest budget.`
                      ]
                    }
                  ].map(({ key, label, paragraphs }) => (
                    <Accordion
                      key={key}
                      data-testid={`lender-consent-faq-${key}`}
                      expanded={expandedAccordion === key}
                      onChange={handleAccordionChange(key)}
                      elevation={0}
                      sx={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px !important',
                        m: '0 !important',
                        '&.Mui-expanded': { m: '0 !important' },
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        data-testid={`lender-consent-faq-summary-${key}`}
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: '18px', color: '#003F44' }} />}
                        sx={{
                          minHeight: '44px',
                          px: 1.75,
                          '& .MuiAccordionSummary-content': { my: 1 },
                          '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 }
                        }}
                      >
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#003F44' }}>{label}</Typography>
                      </AccordionSummary>
                      <AccordionDetails data-testid={`lender-consent-faq-details-${key}`} sx={{ px: 1.75, pt: 0, pb: 1.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          {paragraphs.map((para, idx) => (
                            <Typography key={idx} sx={{ fontSize: '12px', color: '#4B5563', lineHeight: 1.45 }}>
                              {para}
                            </Typography>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Box>

              {/* Sticky footer — separate from BEFORE YOU DECIDE */}
              <Box
                data-testid="lender-consent-decision-footer"
                sx={{
                  flexShrink: 0,
                  borderTop: '1px solid #E5E7EB',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.06)',
                  px: { xs: 2.75, sm: 3.5, md: 4 },
                  pt: 2,
                  pb: { xs: 2.75, sm: 3.5, md: 4 },
                }}
              >
                <Typography sx={{ fontSize: '11.5px', color: '#6B7280', lineHeight: 1.4, mb: 2.25, textAlign: 'center' }}>
                  By continuing, you agree to the{' '}
                  <Box
                    component="a"
                    href="#"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setTermsOpen(true);
                    }}
                    sx={{ fontWeight: 600, textDecoration: 'underline', color: '#111827', cursor: 'pointer' }}
                  >
                    Terms and conditions
                  </Box>{' '}
                  and{' '}
                  <Box
                    component="a"
                    href="#"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setPrivacyOpen(true);
                    }}
                    sx={{ fontWeight: 600, textDecoration: 'underline', color: '#111827', cursor: 'pointer' }}
                  >
                    Privacy policy
                  </Box>{' '}
                  of IE Hub.
                </Typography>

                {apiError && (
                  <Typography sx={{ fontSize: '12px', color: '#DC2626', mb: 1.5, textAlign: 'center' }}>
                    {apiError}
                  </Typography>
                )}

                <Button
                  fullWidth variant="contained"
                  onClick={() => void handleDecision('ACCEPTED')}
                  disabled={isSubmitting}
                  data-testid="lender-consent-btn-allow-budget"
                  sx={{
                    backgroundColor: '#003F44', color: '#FFFFFF', height: '46px', borderRadius: '8px',
                    fontSize: '14.5px', fontWeight: 600, textTransform: 'none', boxShadow: 'none', mb: 1.5,
                    '&:hover': { backgroundColor: '#002B2E', boxShadow: 'none' }
                  }}
                >
                  {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Allow access to my latest budget'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    onClick={() => setDeclineDialogOpen(true)}
                    disabled={isSubmitting}
                    data-testid="lender-consent-btn-decline-link"
                    sx={{
                      fontSize: '12.5px',
                      color: '#6B7280',
                      textTransform: 'none',
                      textDecoration: 'underline',
                      minWidth: 0,
                      px: 1,
                      py: 0.5,
                      '&:hover': {
                        color: '#003F44',
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #003F44',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    Decline the request
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* ── SCREEN: Shared / Allowed ─────────────────────────────────── */}
          {cardState === 'allowed' && (
            <Box data-testid="lender-consent-state-allowed" sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 50, height: 50, borderRadius: '50%', backgroundColor: '#0A5C36', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                <CheckIcon sx={{ fontSize: '28px', color: '#FFFFFF' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', color: '#111827', lineHeight: 1.3, mb: 0.75 }}>
                You&apos;ve shared your budget with {organisationName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '12.5px', lineHeight: 1.45, mb: 0.5 }}>
                {organisationName} can now access your latest budget.
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', mb: 2.5 }}>
                {confirmedAt || (new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) + ' at ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + '.')}
              </Typography>
              {renderLenderContactCard()}
              {renderBottomTealAction()}
            </Box>
          )}

          {/* ── SCREEN: Declined ─────────────────────────────────────────── */}
          {cardState === 'declined' && (
            <Box data-testid="lender-consent-state-declined" sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 50, height: 50, borderRadius: '50%', backgroundColor: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                <CloseIcon sx={{ fontSize: '26px', color: '#FFFFFF' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', color: '#111827', lineHeight: 1.3, mb: 0.75 }}>
                You&apos;ve declined {declineOrgLabel}&apos;s request
              </Typography>
              <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '12.5px', lineHeight: 1.45, mb: 0.5 }}>
                Nothing has been shared, so {declineOrgLabel} cannot access your latest budget.
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', mb: 2.5 }}>
                {confirmedAt || (new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) + ' at ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + '.')}
              </Typography>
              {renderLenderContactCard()}
              {renderBottomTealAction('declined')}
            </Box>
          )}

          {/* ── SCREEN: Expired ──────────────────────────────────────────── */}
          {cardState === 'expired' && (
            <Box data-testid="lender-consent-state-expired" sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 50, height: 50, borderRadius: '50%', backgroundColor: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                <ScheduleIcon sx={{ fontSize: '28px', color: '#FFFFFF' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', color: '#111827', lineHeight: 1.3, mb: 0.75 }}>
                This link has expired
              </Typography>
              <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '12.5px', lineHeight: 1.45, mb: 2.5 }}>
                {apiError ? (
                  apiError
                ) : (
                  <>
                    
                    The{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      link has expired
                    </Box>
                    , so nothing has been shared.
                  </>
                )}
              </Typography>
              {renderBottomTealAction('expired')}
            </Box>
          )}

          {/* ── SCREEN: Incorrect Postcode ────────────────────────────────── */}
          {cardState === 'incorrect_postcode' && (
            <Box data-testid="lender-consent-state-incorrect-postcode" sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  backgroundColor: '#D32F2F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto'
                }}
              >
                <PersonXIcon />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '19px', color: '#E11D48', lineHeight: 1.3, mb: 1 }}>
                Incorrect post-code
              </Typography>
              <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '12.5px', lineHeight: 1.45, mb: 2 }}>
                The postcode you entered did not match the address we have for this request. To keep your information safe, this link can no longer be used. Nothing has been shared with {organisationName}.
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#6B7280', mb: 2.5 }}>
                {postcodeExpiredAt || formatPostcodeExpiredTimestamp()}
              </Typography>
              {renderLenderContactCard()}
              {renderBottomTealAction('incorrect_postcode')}
            </Box>
          )}
        </Paper>
        </Box>
      </Box>

      {/* ── Decline Confirmation Modal ─────────────────────────────────────── */}
      <Dialog
        open={declineDialogOpen}
        onClose={() => setDeclineDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        container={() => leftSectionRef.current ?? document.body}
        disableScrollLock
        sx={{
          position: 'absolute',
          '& .MuiBackdrop-root': {
            position: 'absolute'
          },
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1.5,
            boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
            m: 2,
            width: '100%',
            maxWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, pt: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#111827' }}>Decline this request?</Typography>
          <IconButton size="small" onClick={() => setDeclineDialogOpen(false)} sx={{ color: '#6B7280' }}>
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
            <Typography sx={{ fontSize: '13px', color: '#4B5563', lineHeight: 1.5 }}>
              {`If you decline, ${declineOrgLabel} won't see your latest budget. You can still share it later - ask ${declineOrgLabel} for a new link, or sign in to IE Hub and share it from your account.`}
            </Typography>
          </Box>

          {apiError && (
            <Typography sx={{ fontSize: '12px', color: '#DC2626', mb: 1.5, textAlign: 'center' }}>
              {apiError}
            </Typography>
          )}

          <Button
            fullWidth variant="contained"
            onClick={() => setDeclineDialogOpen(false)}
            sx={{
              backgroundColor: '#003F44', color: '#FFFFFF', height: '44px', borderRadius: '8px',
              fontSize: '14px', fontWeight: 600, textTransform: 'none', boxShadow: 'none', mb: 1.5,
              '&:hover': { backgroundColor: '#002B2E', boxShadow: 'none' }
            }}
          >
            Cancel
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={() => void handleDecision('DECLINED')}
              disabled={isSubmitting}
              data-testid="lender-consent-btn-confirm-decline"
              sx={{
                fontSize: '12.5px',
                color: '#6B7280',
                textTransform: 'none',
                textDecoration: 'none',
                minWidth: 0,
                px: 1,
                py: 0.5,
                opacity: isSubmitting ? 0.6 : 1,
                '&:hover': {
                  color: '#DC2626',
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
                '&:focus-visible': {
                  outline: '2px solid #003F44',
                  outlineOffset: 2,
                },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={12} sx={{ color: '#6B7280', mr: 0.75 }} />
                  Declining…
                </>
              ) : (
                'Decline the request'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Right Carousel ─────────────────────────────────────────────────── */}
      <Box
        data-testid="lender-consent-right-carousel"
        sx={{
          flex: '0 0 auto', width: 'max(5vw, calc(100vh * 9 / 16))',
          display: { xs: 'none', md: 'flex' }, alignItems: 'flex-start',
          justifyContent: 'flex-end', position: 'relative', height: '100vh',
          overflow: 'hidden', backgroundColor: '#003F44'
        }}
      >
        <Box
          onTouchStart={onTouchStartCarousel}
          onTouchEnd={onTouchEndCarousel}
          sx={{ width: '100%', aspectRatio: '9 / 16' }}
        >
          <Box
            component="img"
            src={carouselImages[currentImageIndex]}
            alt="Carousel slideshow"
            sx={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'left center', transition: 'opacity 0.5s ease-in-out' }}
          />
        </Box>

        <Box sx={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)', display: 'flex', justifyContent: 'center', gap: 1.5, zIndex: 2 }}>
          {carouselImages.map((_, index) => (
            <Box
              key={index}
              data-testid={`lender-consent-carousel-dot-${index}`}
              onClick={() => setCurrentImageIndex(index)}
              sx={{
                width: 8, height: 8, borderRadius: '50%', cursor: 'pointer',
                backgroundColor: index === currentImageIndex ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── Terms, Privacy, and FAQ Drawers / Sliders ──────────────────────── */}
      <InfoDrawer open={termsOpen} onClose={() => setTermsOpen(false)} content={termsContent} />
      <InfoDrawer open={privacyOpen} onClose={() => setPrivacyOpen(false)} content={privacyContent} />
      <FAQPanel open={faqOpen} onClose={() => setFaqOpen(false)} content={faqContent} />

      {/* ── Top-Right Incorrect Postcode Alert ────────────────────────────── */}
      <Snackbar
        open={postcodeAlertOpen}
        autoHideDuration={6000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          setPostcodeAlertOpen(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 9999
        }}
      >
        <Box
          data-testid="lender-consent-postcode-alert"
          sx={{
            backgroundColor: '#EF6C00',
            color: '#FFFFFF',
            px: { xs: 2.25, sm: 3 },
            py: { xs: 1.2, sm: 1.4 },
            borderRadius: '50px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            boxShadow: '0 4px 18px rgba(239, 108, 0, 0.35)',
            maxWidth: 'calc(100vw - 32px)'
          }}
        >
          <WarningAmberIcon sx={{ color: '#FFFFFF', fontSize: '24px', flexShrink: 0 }} />
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: { xs: '13.5px', sm: '15px' },
              fontWeight: 600,
              lineHeight: 1.3,
              letterSpacing: '-0.1px'
            }}
          >
            Incorrect postcode. Please check and try again
          </Typography>
        </Box>
      </Snackbar>
    </Box>
  );
}
